/**
 * Panel + PanelRow — the light card and the one row shape everything inside it uses.
 *
 * A row is label-left / control-right against a fixed label column, so every control in a panel
 * starts on the same x and the card stays short. (Label-above was the first cut; six stacked rows
 * made the card twice as tall as it needed to be.)
 */

import { forwardRef, type ReactNode } from 'react';
import { PANEL_SURFACE, PANEL_STACK, ROW_LABEL, ROW_LABEL_W, ROW_VALUE } from './tokens';

interface PanelProps {
  children: ReactNode;
  className?: string;
}

export const Panel = forwardRef<HTMLDivElement, PanelProps>(function Panel({ children, className }, ref) {
  return <div ref={ref} className={`${PANEL_SURFACE} ${className ?? ''}`}>{children}</div>;
});

/** The vertical stack rows live in — its own component so a card can have several (a collapsing
 *  body under a fixed header, for instance). */
export function PanelStack({ children, className }: PanelProps) {
  return <div className={`${PANEL_STACK} ${className ?? ''}`}>{children}</div>;
}

interface PanelRowProps {
  label?: string;
  /** Second line under the label (a live value, a percentage). */
  value?: string;
  children: ReactNode;
}

export function PanelRow({ label, value, children }: PanelRowProps) {
  const header = (label || value) && (
    <div className="leading-tight">
      <div className={ROW_LABEL} title={label}>{label}</div>
      {value && <div className={ROW_VALUE}>{value}</div>}
    </div>
  );

  return (
    <div className="flex items-center gap-2">
      {header && <div className={`shrink-0 ${ROW_LABEL_W}`}>{header}</div>}
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
}
